import '@bifeai/ui/globals.css';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, ToastProvider } from '@bifeai/ui';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router';
import { isApiError } from './lib/api';
import { installGlobalErrorReporting, reportClientError } from './lib/report';
import { router } from './router';

installGlobalErrorReporting();

// La aplicación queda guardada en la computadora: el punto de venta abre aunque se corte internet.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  });
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Sesión vencida en cualquier pantalla: volver a pedir "me" lleva al login.
      // (Si el que falla es "me", no se vuelve a pedir: evita un bucle.)
      if (isApiError(error, 'UNAUTHENTICATED')) {
        if (query.queryKey[0] !== 'me') void queryClient.invalidateQueries({ queryKey: ['me'] });
      }
      // Los errores 5xx ya los registra la API; acá solo se reportan fallas de red o de pantalla.
      else if (!isApiError(error)) reportClientError(error);
    },
  }),
  defaultOptions: {
    queries: {
      retry: (count, error) => !isApiError(error) && count < 2,
      refetchOnWindowFocus: false,
      // Al volver a una pantalla se muestra al instante lo último cargado y se actualiza por detrás.
      staleTime: 15_000,
      gcTime: 30 * 60_000,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('Falta el elemento #root');

createRoot(root).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
